import { Image, ScrollView, StyleSheet, Text, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

export type Memory = {
  id: string;
  imageUri: string;
  caption?: string;
};

export type MemoriesSectionProps = {
  memories?: Memory[];
};

/**
 * MemoriesSection - Displays a horizontal scroll of family memory photos
 */
export function MemoriesSection({ memories = [] }: MemoriesSectionProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (memories.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Text style={[styles.heading, { color: palette.text }]}>Memories</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {memories.map((memory) => (
          <View key={memory.id} style={[styles.memoryCard, { backgroundColor: palette.cardBackground }]}>
            <Image
              source={{ uri: memory.imageUri }}
              style={styles.memoryImage}
              resizeMode="cover"
            />
            {memory.caption && (
              <Text style={[styles.caption, { color: palette.textMuted }]} numberOfLines={2}>
                {memory.caption}
              </Text>
            )}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: Spacing.lg,
  },
  heading: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: Spacing.md,
  },
  scrollContent: {
    gap: Spacing.md,
    paddingVertical: Spacing.xs,
  },
  memoryCard: {
    width: 180,
    borderRadius: BorderRadius.sm,
    overflow: 'hidden',
    // backgroundColor is set dynamically based on color scheme
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  memoryImage: {
    width: 180,
    height: 180,
  },
  caption: {
    fontSize: 12,
    padding: Spacing.sm,
    lineHeight: 16,
  },
});
